import { formatDisplayLabel } from './camelCaseLabel';

export function hasValidCheckInCheckOut(
  checkInAt: string | Date | null | undefined,
  checkOutAt: string | Date | null | undefined,
): boolean {
  if (checkInAt == null || checkOutAt == null || checkInAt === '' || checkOutAt === '') {
    return false;
  }
  const checkIn = checkInAt instanceof Date ? checkInAt.getTime() : Date.parse(String(checkInAt));
  const checkOut = checkOutAt instanceof Date ? checkOutAt.getTime() : Date.parse(String(checkOutAt));
  return Number.isFinite(checkIn) && Number.isFinite(checkOut) && checkOut > checkIn;
}

export function configuredDurationDays(
  durations: Array<{
    isHalfDay: boolean;
    durationDays: number;
    requiresExplicitRule?: boolean;
  }>,
  isHalfDay: boolean,
): number | null {
  const match = durations.find(
    duration =>
      duration.isHalfDay === isHalfDay && duration.requiresExplicitRule !== true,
  );
  if (
    match == null ||
    !Number.isFinite(match.durationDays) ||
    match.durationDays <= 0
  ) {
    return null;
  }
  return match.durationDays;
}

export function compensatoryCreditLeaveName(
  balances: Array<{
    receivesCompensatoryCredit?: boolean | null;
    leaveTypeName?: string | null;
  }>,
): string | null {
  const match = balances.find(balance => balance.receivesCompensatoryCredit);
  const name = match?.leaveTypeName?.trim();
  return name || null;
}

export function compensatoryLeaveDaysFromAttendance(input: {
  netWorkMinutes: number;
  expectedWorkMinutes: number;
  halfDayWorkMinutes: number | null;
  fullDayLeaveDays: number;
  halfDayLeaveDays: number;
}): number {
  if (
    !Number.isFinite(input.netWorkMinutes) ||
    input.netWorkMinutes <= 0 ||
    !Number.isFinite(input.expectedWorkMinutes) ||
    input.expectedWorkMinutes <= 0 ||
    !Number.isFinite(input.fullDayLeaveDays) ||
    input.fullDayLeaveDays <= 0
  ) {
    return 0;
  }
  if (input.netWorkMinutes >= input.expectedWorkMinutes) {
    return input.fullDayLeaveDays;
  }
  if (
    input.halfDayWorkMinutes != null &&
    Number.isFinite(input.halfDayWorkMinutes) &&
    input.halfDayWorkMinutes > 0 &&
    Number.isFinite(input.halfDayLeaveDays) &&
    input.halfDayLeaveDays > 0 &&
    input.netWorkMinutes >= input.halfDayWorkMinutes
  ) {
    return input.halfDayLeaveDays;
  }
  return 0;
}

export function resolveCompensatoryLeaveCredit(input: {
  hasValidCheckInCheckOut: boolean;
  netWorkMinutes: number;
  expectedWorkMinutes: number;
  halfDayWorkMinutes: number | null;
  fullDayLeaveDays: number;
  halfDayLeaveDays: number;
  leaveTypeCode: string;
}): { leaveTypeCode: string; days: number } | null {
  if (!input.hasValidCheckInCheckOut || !input.leaveTypeCode.trim()) {
    return null;
  }
  const days = compensatoryLeaveDaysFromAttendance(input);
  if (days <= 0) {
    return null;
  }
  return { leaveTypeCode: input.leaveTypeCode, days };
}

export function compensatoryLeaveTypeLabel(
  nameOrCode: string | null | undefined,
): string | null {
  const label = formatDisplayLabel(nameOrCode);
  return label || null;
}

export function compensatoryWorkPolicyText(input: {
  leaveTypeName: string | null;
  fullDayLeaveDays: number | null;
  halfDayLeaveDays: number | null;
}): string {
  const leaveType =
    compensatoryLeaveTypeLabel(input.leaveTypeName) ||
    'the configured compensatory credit leave type';
  const fullCredit =
    input.fullDayLeaveDays != null
      ? `${input.fullDayLeaveDays} ${leaveType}`
      : `a full-day amount of ${leaveType}`;
  const halfCredit =
    input.halfDayLeaveDays != null
      ? `${input.halfDayLeaveDays} ${leaveType}`
      : `a half-day amount of ${leaveType}`;
  return [
    'Request approval before the work date or on the work date itself.',
    'Backdated requests are not allowed after the work date has ended.',
    'You can apply when the date is Leave, a Holiday, or a Weekly Off.',
    'Approval does not add leave.',
    'After you check in and check out on that date, the system uses your actual attendance duration against the configured expected work duration and half-day duration.',
    `Completing the expected work duration credits ${fullCredit}.`,
    `Working at least the half-day duration but less than expected credits ${halfCredit}.`,
    'Working below the half-day duration does not add compensatory leave.',
    'Unused Comp Off first offsets same-month LOP and expires at month end.',
  ].join(' ');
}
